import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';

// Components
import LoginPage from './components/auth/LoginPage';
import Dashboard from './components/dashboard/Dashboard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import NavBar from './components/layout/NavBar';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import AdminPanel from './components/admin/AdminPanel';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E86AB',
      light: '#5BA3C7',
      dark: '#1E5F7B',
    },
    secondary: {
      main: '#FF9933',
      light: '#FFB366',
      dark: '#CC7A29',
    },
    success: {
      main: '#138808',
      light: '#4CAF50',
      dark: '#0E6B06',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      color: '#2E86AB',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#666',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#2E86AB',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider 
          maxSnack={3}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <AuthProvider>
            <WebSocketProvider>
              <Router>
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <NavBar />
                  
                  {/* India-only compliance banner */}
                  <Box
                    sx={{
                      background: 'linear-gradient(to bottom, #FF671F 0%, #FF671F 33.33%, #FFFFFF 33.33%, #FFFFFF 66.66%, #046A38 66.66%, #046A38 100%)',
                      color: '#000080',
                      fontWeight: 700,
                      fontSize: '16px',
                      textAlign: 'center',
                      padding: '10px 14px',
                      textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      width: '100%',
                      height: 'auto',
                      minHeight: '40px',
                    }}
                  >
                    ⚠️ IMPORTANT NOTICE: This service is ONLY for India-based users • We have NOT implemented GDPR or HIPAA compliance • This is NOT compliant for EU or US users
                  </Box>

                  <Box component="main" sx={{ flex: 1, pt: 2 }}>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/auth/callback" element={<LoginPage />} />
                      <Route path="/auth/callback/*" element={<LoginPage />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin" 
                        element={
                          <ProtectedRoute adminOnly>
                            <AdminPanel />
                          </ProtectedRoute>
                        } 
                      />
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </Box>
                </Box>
              </Router>
            </WebSocketProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;


